import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { VideoDownloadLink } from '../models/video-download-link.interface';
import { AthleticFollowInfo } from '../models/athletic-follow-info.interface';
import { AnetAuthService, AthleticUser } from '@athleticnet/athletic-live-api';

@Injectable({
  providedIn: 'root',
})
export class AnetLiveService {
  private http: HttpClient = inject(HttpClient);
  private anetAuthService = inject(AnetAuthService);

  async getFollowInfo(sport: string, athleticNetMeetIdStr: string): Promise<AthleticFollowInfo | null> {
    // this.http.post<any>(`${environment.anetAPIEndpoint}/v1/Live2/GetUserData`)
    if (athleticNetMeetIdStr) {
      return firstValueFrom(
        this.http
          .get(
            `${environment.anetAPIEndpoint}/v1/Live2/GetFollowInfo?sport=${sport}&meetId=${athleticNetMeetIdStr}`,
            {},
          )
          .pipe(
            map((data: any) => {
              if (!data) return null;
              const obj: AthleticFollowInfo = data;
              return obj;
            }),
          ),
      );
    } else {
      return null;
    }
  }

  /**
   * @deprecated Core auth moved to `AnetAuthService` in
   * `@athleticnet/athletic-live-api`. Use `AnetAuthService.getUserData()`.
   */
  async getUserData(): Promise<AthleticUser | null> {
    return this.anetAuthService.getUserData();
  }

  async getVideoDownloadInfo(videoId: string): Promise<VideoDownloadLink | null> {
    // this.http.post<any>(`${environment.anetAPIEndpoint}/v1/Live2/GetUserData`)
    return firstValueFrom(
      this.http.get(`${environment.anetAPIEndpoint}/v1/Live2/VideoDownloadInfo?videoId=${videoId}`, {}).pipe(
        map((data: any) => {
          if (!data) return null;
          const obj: VideoDownloadLink = data;
          return obj;
        }),
      ),
    );
  }

  async userIsAllowedVideoDownloadEmail(videoGuid: string): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get(`${environment.anetAPIEndpoint}/v1/video/IsAllowedDownload?guid=${videoGuid}`, {
          observe: 'response',
        })
        .pipe(
          map((resp) => resp.ok),
          catchError(() => of(false)), // Silently catch any errors and return false
        ),
    );
  }

  async sendVideoDownloadEmail(videoGuid: string): Promise<boolean> {
    try {
      const resp = await firstValueFrom(
        this.http.post(`${environment.anetAPIEndpoint}/v1/video/EmailDownloadLink?guid=${videoGuid}`, null, {
          observe: 'response',
        }),
      );

      return resp.ok;
    } catch (error) {
      return false;
    }
  }

  async getSignedJwMedia(guid: string): Promise<{
    url: string;
    overlayUrl?: string;
    overlayLink?: string;
  }> {
    return firstValueFrom(
      this.http.get(`${environment.anetAPIEndpoint}/v1/jwplayer/mediaurl?guid=${guid}`, {}).pipe(
        map((data: any) => {
          if (!data) return { url: '', overlayUrl: undefined, overlayLink: undefined };
          return { url: data.url || '', ...data.overlayOptions };
        }),
      ),
    );
  }

  async startNotifyingAthlete(linkId: string | number): Promise<boolean> {
    return this.setNotify('A', linkId, true);
  }

  async stopNotifyingAthlete(linkId: string | number): Promise<boolean> {
    return this.setNotify('A', linkId, false);
  }

  async startFollowingAthlete(linkId: string | number, source?: string): Promise<boolean> {
    return this.startFollowing('A', linkId, source);
  }

  async stopFollowingAthlete(linkId: string | number): Promise<boolean> {
    return this.stopFollowing('A', linkId);
  }

  async startNotifyingTeam(linkId: string | number, sport: string): Promise<boolean> {
    return this.setNotify(sport, linkId, true);
  }

  async stopNotifyingTeam(linkId: string | number, sport: string): Promise<boolean> {
    return this.setNotify(sport, linkId, false);
  }

  async startFollowingTeam(teamId: string | number, sport: string, source?: string): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get(
          `${environment.anetAPIEndpoint}/v1/SocialProfile/StartFollowingTeam?teamId=${teamId}&sport=${sport}` +
            (source ? `&source=${source}` : ''),
          {},
        )
        .pipe(
          map((data: any) => {
            const obj: boolean = data;
            return obj;
          }),
        ),
    );
  }

  async stopFollowingTeam(teamId: string | number, sport: string): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get(`${environment.anetAPIEndpoint}/v1/SocialProfile/StopFollowingTeam?teamId=${teamId}&sport=${sport}`, {})
        .pipe(
          map((data: any) => {
            const obj: boolean = data;
            return obj;
          }),
        ),
    );
  }

  protected async setNotify(type: string, linkId: string | number, notify: boolean): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get(
          `${environment.anetAPIEndpoint}/v1/SocialFollow/SetNotify?type=${type}&linkId=${linkId}&notify=${notify}`,
          {},
        )
        .pipe(
          map((data: any) => {
            const obj: boolean = data;
            return obj;
          }),
        ),
    );
  }

  protected async startFollowing(type: string, linkId: string | number, source?: string): Promise<boolean> {
    // this.http.post<any>(`${environment.anetAPIEndpoint}/v1/Live2/GetUserData`)
    return firstValueFrom(
      this.http
        .get(
          `${environment.anetAPIEndpoint}/v1/SocialFollow/StartFollowing?type=${type}&linkId=${linkId}` +
            (source ? `&source=${source}` : ''),
          {},
        )
        .pipe(
          map((data: any) => {
            const obj: boolean = data;
            return obj;
          }),
        ),
    );
  }

  protected async stopFollowing(type: string, linkId: string | number): Promise<boolean> {
    // this.http.post<any>(`${environment.anetAPIEndpoint}/v1/Live2/GetUserData`)
    return firstValueFrom(
      this.http
        .get(`${environment.anetAPIEndpoint}/v1/SocialFollow/StopFollowing?type=${type}&linkId=${linkId}`, {})
        .pipe(
          map((data: any) => {
            const obj: boolean = data;
            return obj;
          }),
        ),
    );
  }
}
